import Fuse from "fuse.js";
import { createSelector } from "reselect";
import { EMPTY_ARRAY } from "lib";

import { getSideLoadedBusinesses } from "./root";

export const getSideLoadedBusinessById =
  (recordId: string) => (state: unknown) =>
    getSideLoadedBusinesses(state)[recordId] || null;

export const getSideLoadedBusinessesCount = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) =>
    Object.values(sideLoadedBusinesses).length,
);

// Fuse options for searching side loaded businesses.
// These should be pretty good for finding a sweet spot of results.
// One common idiom it needs to handle is finding "Kevin Turner" when searching for any of "Turner, K", "Turner Associates", "Kevin T", etc - by tokeninzing down to
// simplified text, we should be able to get some results for all of these, without too much noise.
const FUSE_OPTIONS = {
  keys: [
    { name: "name", weight: 0.5 },
    { name: "formattedName", weight: 0.5 },
    { name: "abn", weight: 0.2 },
  ],
  useExtendedSearch: true,
  threshold: 0.2,
  minMatchCharLength: 2,
  isCaseSensitive: false,
};

// Tokenize the search name field into a format that Fuse can easily search.
// - strip punctuation
// - convert whitespace to single space
function formatSearchNameField(name: string) {
  return name
    .replace(/[^\w\s]/g, " ")
    .trim()
    .split(/\s+/)
    .join(" ");
}

// Tokenize the search query into a format that Fuse can understand - basically
// - strip punctuation
// - split on space
// - prepending an `'` to each term, which is an "or" in terms of the `useExtendedSearch` option
function formatSearchQuery(query: string) {
  return query
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(/\s+/)
    .map(term => `'${term}`)
    .join(" ");
}

interface SearchFormattedSideLoadedBusiness extends SideLoadedBusiness {
  formattedName: string;
}
export interface SideLoadedBusinessSearchResult {
  item: SearchFormattedSideLoadedBusiness;
}

export const selectSideLoadedBusinessSearchIndex = createSelector(
  [getSideLoadedBusinesses],
  (sideLoadedBusinesses: Record<string, SideLoadedBusiness>) => {
    const searchFormattedBusinesses = Object.values(sideLoadedBusinesses).map(
      slb =>
        ({
          ...slb,
          formattedName: formatSearchNameField(slb.name),
        }) as SearchFormattedSideLoadedBusiness,
    );
    return new Fuse(searchFormattedBusinesses, FUSE_OPTIONS);
  },
);

export const searchSideLoadedBusinesses = (query: string) => (state: State) => {
  const formattedQuery = formatSearchQuery(query);
  if (!formattedQuery || formattedQuery.length < 2) {
    return EMPTY_ARRAY;
  }
  const index = selectSideLoadedBusinessSearchIndex(state);
  return index.search(formattedQuery);
};

export const searchSideLoadedBusinessesCount =
  (query: string) => (state: State) =>
    searchSideLoadedBusinesses(query)(state).length;

export const selectSideLoadedBusinessMappableFieldsByIdLookup = createSelector(
  [getSideLoadedBusinesses],
  sideLoadedBusinesses => {
    // Optimization here - all the mappable fields are the same for all side loaded businesses, so we can just get them once, then inject that
    // into the lookup
    const firstBusiness = Object.values(
      sideLoadedBusinesses,
    )?.[0] as SideLoadedBusiness;
    if (!firstBusiness) {
      return {};
    }

    const mappableFields = Object.keys(firstBusiness.rawData).sort((a, b) => {
      // Shunt name and abn to the top of the list, but keep everythign else in order.
      // TODO - be great to keep this the actual CSV order...
      if (a === "name" || a === "abn") return -1;
      if (b === "name" || b === "abn") return 1;
      return 0;
    });

    return Object.keys(sideLoadedBusinesses).reduce(
      (acc, sideLoadedBusinessId) => {
        acc[sideLoadedBusinessId] = mappableFields;
        return acc;
      },
      {},
    );
  },
);

export const getSideLoadedBusinessMappableFieldsById =
  (recordId: string) => (state: State) =>
    selectSideLoadedBusinessMappableFieldsByIdLookup(state)[recordId] ||
    EMPTY_ARRAY;

const selectSideLoadedBusinessMappableOptionsByIdLookup = createSelector(
  [getSideLoadedBusinesses, selectSideLoadedBusinessMappableFieldsByIdLookup],
  (sideLoadedBusinesses, mappableFieldsByIdLookup) => {
    return Object.values(sideLoadedBusinesses).reduce(
      (acc, sideLoadedBusiness: SideLoadedBusiness) => {
        const mappableFields = mappableFieldsByIdLookup[sideLoadedBusiness.id];
        // Show only mappable fields with data.
        acc[sideLoadedBusiness.id] = mappableFields
          .filter(field => Boolean(sideLoadedBusiness?.rawData[field]))
          .map(field => ({
            label: sideLoadedBusiness?.rawData[field] || "No data",
            value: field,
            disabled: !sideLoadedBusiness?.rawData[field],
          }));
        return acc;
      },
      {},
    );
  },
);

export const getSideLoadedBusinessMappableOptionsById =
  (recordId: string) => (state: State) => {
    return (
      selectSideLoadedBusinessMappableOptionsByIdLookup(state)[recordId] ||
      EMPTY_ARRAY
    );
  };
